/**
 * 一元配置分散分析（One-way ANOVA）の計算関数
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';

/**
 * ANOVAの結果
 */
export interface ANOVAResult {
  f: number; // F統計量
  pValue: number; // P値
  dfBetween: number; // 群間の自由度
  dfWithin: number; // 群内の自由度
  ssBetween: number; // 群間平方和（SSB）
  ssWithin: number; // 群内平方和（SSW）
  msBetween: number; // 群間平均平方（MSB）
  msWithin: number; // 群内平均平方（MSW）
  design?: 'independent' | 'repeated' | 'lmm';
  ssSubject?: number;
  dfSubject?: number;
  msSubject?: number;
  varianceComponents?: {
    randomIntercept: number;
    residual: number;
  };
  groups: Array<{
    name: string;
    mean: number;
    n: number;
    stdDev: number;
  }>; // 各群の統計量
}

export interface TwoWayANOVATerm {
  name: string;
  ss: number;
  df: number;
  ms: number;
  f: number;
  pValue: number;
  errorDf?: number;
}

export interface TwoWayANOVAResult {
  factorA: TwoWayANOVATerm;
  factorB: TwoWayANOVATerm;
  interaction: TwoWayANOVATerm;
  within: TwoWayANOVATerm;
  errorTerms?: TwoWayANOVATerm[];
  design?: 'independent' | 'mixed' | 'full-repeated' | 'lmm';
  /** LMMでは固定効果のSS/MSは意味を持たないため、UIで表示しない */
  omitSSMSTable?: boolean;
  subjectCount?: number;
  varianceComponents?: {
    randomIntercept: number;
    residual: number;
  };
  overallMean: number;
  factorALevels: string[];
  factorBLevels: string[];
  cells: Array<{
    factorA: string;
    factorB: string;
    n: number;
    mean: number;
  }>;
}

/**
 * 対数ガンマ関数（Lanczos近似）
 */
function logGamma(z: number): number {
  if (z < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * z)) - logGamma(1 - z);
  }
  z -= 1;
  const g = 7;
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028,
    771.32342877765313, -176.61502916214059, 12.507343278686905,
    -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
  ];
  let x = c[0];
  for (let i = 1; i < g + 2; i += 1) {
    x += c[i] / (z + i);
  }
  const t = z + g + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

/**
 * 正則化不完全ベータ関数 I_x(a, b)
 * 級数: I_x(a,b) = (x^a (1-x)^b)/(a B(a,b)) * 2F1(1, a+b; a+1; x)
 * 即ち sum の term_n = term_{n-1} * (a+b+n-1)/(a+n) * x, term_0=1
 * x > (a+1)/(a+b+2) のとき対称性 I_x(a,b) = 1 - I_{1-x}(b,a) で安定化
 */
function regularizedIncompleteBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const threshold = (a + 1) / (a + b + 2);
  if (x > threshold) {
    return 1 - regularizedIncompleteBeta(1 - x, b, a);
  }
  const logBeta = logGamma(a) + logGamma(b) - logGamma(a + b);
  const logFront = a * Math.log(x) + b * Math.log(1 - x) - Math.log(a) - logBeta;
  let sum = 1;
  let term = 1;
  const maxIter = 500;
  const eps = 1e-14;
  for (let n = 1; n < maxIter; n += 1) {
    term *= ((a + b + n - 1) / (a + n)) * x;
    sum += term;
    if (Math.abs(term) <= eps * Math.abs(sum)) break;
  }
  const result = Math.exp(logFront) * sum;
  return Math.max(0, Math.min(1, result));
}

/**
 * F分布の上側確率（P値）
 * 上側 = 1 - CDF = I_{1-x}(df2/2, df1/2)。x が 1 に近いときはこの形で計算して
 * アンダーフローを避け、極小 p を指数表記で表示できるようにする。
 */
export function calculatePValue(f: number, df1: number, df2: number): number {
  if (f <= 0 || !Number.isFinite(f) || df1 <= 0 || df2 <= 0) {
    return 1;
  }
  const x = (df1 * f) / (df2 + df1 * f);
  const a = df1 / 2;
  const b = df2 / 2;
  if (x >= 1) return 0;
  if (x <= 0) return 1;
  const upperTail = regularizedIncompleteBeta(1 - x, b, a);
  return upperTail;
}

type Matrix = number[][];

const createMatrix = (rows: number, cols: number, fill = 0): Matrix =>
  Array.from({ length: rows }, () => Array.from({ length: cols }, () => fill));

const cloneMatrix = (matrix: Matrix): Matrix => matrix.map((row) => [...row]);

const transpose = (matrix: Matrix): Matrix => {
  if (matrix.length === 0) return [];
  return matrix[0].map((_, col) => matrix.map((row) => row[col]));
};

const matMul = (a: Matrix, b: Matrix): Matrix => {
  const rows = a.length;
  const cols = b[0]?.length ?? 0;
  const mid = b.length;
  const result = createMatrix(rows, cols, 0);
  for (let i = 0; i < rows; i += 1) {
    for (let k = 0; k < mid; k += 1) {
      const aik = a[i][k] ?? 0;
      if (aik === 0) continue;
      for (let j = 0; j < cols; j += 1) {
        result[i][j] += aik * (b[k][j] ?? 0);
      }
    }
  }
  return result;
};

const matVecMul = (matrix: Matrix, vector: number[]): number[] =>
  matrix.map((row) => row.reduce((sum, value, idx) => sum + value * (vector[idx] ?? 0), 0));

const vecDot = (a: number[], b: number[]): number => a.reduce((sum, v, idx) => sum + v * (b[idx] ?? 0), 0);

const matAdd = (a: Matrix, b: Matrix, scale = 1): Matrix => {
  const result = createMatrix(a.length, a[0]?.length ?? 0, 0);
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < (a[0]?.length ?? 0); j += 1) {
      result[i][j] = (a[i][j] ?? 0) + scale * (b[i][j] ?? 0);
    }
  }
  return result;
};

const matInverse = (matrix: Matrix): Matrix => {
  const n = matrix.length;
  const m = cloneMatrix(matrix);
  const inv = createMatrix(n, n, 0);
  for (let i = 0; i < n; i += 1) {
    inv[i][i] = 1;
  }

  for (let i = 0; i < n; i += 1) {
    let pivotRow = i;
    let pivotValue = Math.abs(m[i][i]);
    for (let r = i + 1; r < n; r += 1) {
      const value = Math.abs(m[r][i]);
      if (value > pivotValue) {
        pivotValue = value;
        pivotRow = r;
      }
    }
    if (pivotValue === 0) {
      throw new KeisukeAdapterError('singularMatrix');
    }
    if (pivotRow !== i) {
      [m[i], m[pivotRow]] = [m[pivotRow], m[i]];
      [inv[i], inv[pivotRow]] = [inv[pivotRow], inv[i]];
    }
    const pivot = m[i][i];
    for (let j = 0; j < n; j += 1) {
      m[i][j] /= pivot;
      inv[i][j] /= pivot;
    }
    for (let r = 0; r < n; r += 1) {
      if (r === i) continue;
      const factor = m[r][i];
      for (let j = 0; j < n; j += 1) {
        m[r][j] -= factor * m[i][j];
        inv[r][j] -= factor * inv[i][j];
      }
    }
  }

  return inv;
};

const outerProduct = (a: number[], b: number[]): Matrix => {
  const result = createMatrix(a.length, b.length, 0);
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < b.length; j += 1) {
      result[i][j] = a[i] * b[j];
    }
  }
  return result;
};

const buildTwoWayDesignRow = (
  factorA: string,
  factorB: string,
  factorALevels: string[],
  factorBLevels: string[]
): number[] => {
  const a = factorALevels.length;
  const b = factorBLevels.length;
  const row: number[] = [1];
  for (let i = 1; i < a; i += 1) {
    row.push(factorA === factorALevels[i] ? 1 : 0);
  }
  for (let j = 1; j < b; j += 1) {
    row.push(factorB === factorBLevels[j] ? 1 : 0);
  }
  for (let i = 1; i < a; i += 1) {
    for (let j = 1; j < b; j += 1) {
      const aVal = factorA === factorALevels[i] ? 1 : 0;
      const bVal = factorB === factorBLevels[j] ? 1 : 0;
      row.push(aVal * bVal);
    }
  }
  return row;
};

const buildOneWayDesignRow = (group: string, groupLevels: string[]): number[] => {
  const row: number[] = [1];
  for (let i = 1; i < groupLevels.length; i += 1) {
    row.push(group === groupLevels[i] ? 1 : 0);
  }
  return row;
};

/**
 * 一元配置分散分析（One-way ANOVA）
 */
export function oneWayANOVA(data: { group: string; value: number }[]): ANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  // 群に分割
  const groups: { [key: string]: number[] } = {};
  data.forEach((row) => {
    if (!groups[row.group]) {
      groups[row.group] = [];
    }
    groups[row.group].push(row.value);
  });

  const groupNames = Object.keys(groups);
  const k = groupNames.length; // 群の数

  if (k < 2) {
    throw new KeisukeAdapterError('minTwoGroups');
  }

  // 各群の統計量を計算
  const groupStats = groupNames.map((name) => {
    const values = groups[name];
    const n = values.length;
    const mean = values.reduce((sum, val) => sum + val, 0) / n;
    const variance =
      values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (n - 1);
    const stdDev = Math.sqrt(variance);

    return { name, mean, n, stdDev };
  });

  // 全体の平均値
  const totalMean = data.reduce((sum, row) => sum + row.value, 0) / data.length;
  const N = data.length; // 全体のサンプルサイズ

  // 群間平方和（SSB）
  const ssBetween = groupStats.reduce(
    (sum, group) => sum + group.n * Math.pow(group.mean - totalMean, 2),
    0
  );

  // 群内平方和（SSW）
  const ssWithin = groupStats.reduce((sum, group) => {
    const groupValues = groups[group.name];
    const groupSS = groupValues.reduce(
      (ss, val) => ss + Math.pow(val - group.mean, 2),
      0
    );
    return sum + groupSS;
  }, 0);

  // 自由度
  const dfBetween = k - 1;
  const dfWithin = N - k;
  if (dfWithin <= 0) {
    throw new KeisukeAdapterError('withinDfNonPositive');
  }

  // 平均平方
  const msBetween = ssBetween / dfBetween;
  const msWithin = ssWithin / dfWithin;

  // F統計量
  const f = msBetween / msWithin;

  // P値
  const pValue = calculatePValue(f, dfBetween, dfWithin);

  return {
    f,
    pValue,
    dfBetween,
    dfWithin,
    ssBetween,
    ssWithin,
    msBetween,
    msWithin,
    design: 'independent',
    groups: groupStats,
  };
}

/**
 * 反復測定 1-way ANOVA（被験体内要因）
 */
export function repeatedMeasuresOneWayANOVA(data: {
  subject: string;
  group: string;
  value: number;
}[]): ANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  const groupLevels = Array.from(new Set(data.map((row) => row.group)));
  const subjects = Array.from(new Set(data.map((row) => row.subject)));

  if (groupLevels.length < 2) {
    throw new KeisukeAdapterError('minTwoLevels');
  }
  if (subjects.length < 2) {
    throw new KeisukeAdapterError('insufficientSubjects');
  }

  const subjectMap = new Map<string, Map<string, number>>();
  data.forEach((row) => {
    if (!subjectMap.has(row.subject)) {
      subjectMap.set(row.subject, new Map());
    }
    const groupMap = subjectMap.get(row.subject);
    if (groupMap?.has(row.group)) {
      throw new KeisukeAdapterError('duplicateSubjectLevel');
    }
    groupMap?.set(row.group, row.value);
  });

  subjects.forEach((subject) => {
    const groupMap = subjectMap.get(subject);
    groupLevels.forEach((level) => {
      if (!groupMap?.has(level)) {
        throw new KeisukeAdapterError('rmAnovaMissingWithinSubject');
      }
    });
  });

  const n = subjects.length;
  const k = groupLevels.length;

  const groupValues: Record<string, number[]> = {};
  groupLevels.forEach((level) => {
    groupValues[level] = [];
  });
  data.forEach((row) => {
    groupValues[row.group].push(row.value);
  });

  const groupStats = groupLevels.map((level) => {
    const values = groupValues[level];
    const nGroup = values.length;
    const mean = values.reduce((sum, v) => sum + v, 0) / nGroup;
    const variance =
      values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / (nGroup - 1);
    return { name: level, mean, n: nGroup, stdDev: Math.sqrt(variance) };
  });

  const grandMean = data.reduce((sum, row) => sum + row.value, 0) / data.length;

  const subjectMeans = subjects.map((subject) => {
    const values = Array.from(subjectMap.get(subject)?.values() ?? []);
    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    return { subject, mean };
  });

  const groupMeans = groupStats.reduce((map, group) => {
    map.set(group.name, group.mean);
    return map;
  }, new Map<string, number>());

  const ssTotal = data.reduce((sum, row) => sum + Math.pow(row.value - grandMean, 2), 0);
  const ssBetween = n * groupLevels.reduce((sum, level) => {
    const mean = groupMeans.get(level) ?? grandMean;
    return sum + Math.pow(mean - grandMean, 2);
  }, 0);
  const ssSubject = k * subjectMeans.reduce((sum, subject) => sum + Math.pow(subject.mean - grandMean, 2), 0);
  const ssWithin = ssTotal - ssBetween - ssSubject;

  const dfBetween = k - 1;
  const dfSubject = n - 1;
  const dfWithin = dfBetween * dfSubject;
  if (dfWithin <= 0) {
    throw new KeisukeAdapterError('rmAnovaErrorDfNonPositive');
  }

  const msBetween = ssBetween / dfBetween;
  const msSubject = ssSubject / dfSubject;
  const msWithin = ssWithin / dfWithin;

  const f = msBetween / msWithin;
  const pValue = calculatePValue(f, dfBetween, dfWithin);

  return {
    f,
    pValue,
    dfBetween,
    dfWithin,
    ssBetween,
    ssWithin,
    msBetween,
    msWithin,
    ssSubject,
    dfSubject,
    msSubject,
    design: 'repeated',
    groups: groupStats,
  };
}

/**
 * 1-way LMM（ランダム切片）
 */
export function lmmOneWayANOVA(data: {
  subject: string;
  group: string;
  value: number;
}[]): ANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  const groupLevels = Array.from(new Set(data.map((row) => row.group)));
  const subjects = Array.from(new Set(data.map((row) => row.subject)));

  if (groupLevels.length < 2) {
    throw new KeisukeAdapterError('minTwoLevels');
  }
  if (subjects.length < 2) {
    throw new KeisukeAdapterError('lmmMinSubjects');
  }

  const subjectGroups = new Map<string, { y: number[]; X: Matrix }>();
  data.forEach((row) => {
    const rowX = buildOneWayDesignRow(row.group, groupLevels);
    if (!subjectGroups.has(row.subject)) {
      subjectGroups.set(row.subject, { y: [], X: [] });
    }
    const entry = subjectGroups.get(row.subject);
    entry?.y.push(row.value);
    entry?.X.push(rowX);
  });

  const allValues = data.map((row) => row.value);
  const mean = allValues.reduce((sum, v) => sum + v, 0) / allValues.length;
  const variance =
    allValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / Math.max(1, allValues.length - 1);

  let sigmaE2 = Math.max(variance * 0.5, 1e-6);
  let sigmaB2 = Math.max(variance * 0.5, 1e-6);

  const maxIter = 50;
  const tol = 1e-6;
  const p = buildOneWayDesignRow(groupLevels[0], groupLevels).length;
  let beta = Array.from({ length: p }, () => 0);

  for (let iter = 0; iter < maxIter; iter += 1) {
    let XtVinvX = createMatrix(p, p, 0);
    let XtVinvY = Array.from({ length: p }, () => 0);

    subjectGroups.forEach(({ y, X }) => {
      const n = y.length;
      const a = sigmaE2;
      const b = sigmaB2;
      const weight1 = 1 / a;
      const weight2 = b / (a * (a + n * b));
      const Xt = transpose(X);
      const XtX = matMul(Xt, X);
      const ones = Array.from({ length: n }, () => 1);
      const Xt1 = matVecMul(Xt, ones);
      XtVinvX = matAdd(XtVinvX, XtX, weight1);
      XtVinvX = matAdd(XtVinvX, outerProduct(Xt1, Xt1), -weight2);

      const Xty = matVecMul(Xt, y);
      const sumY = y.reduce((sum, v) => sum + v, 0);
      XtVinvY = XtVinvY.map((value, idx) => value + weight1 * Xty[idx] - weight2 * Xt1[idx] * sumY);
    });

    const XtVinvXInv = matInverse(XtVinvX);
    beta = matVecMul(XtVinvXInv, XtVinvY);

    let sigmaB2Sum = 0;
    let sigmaE2Sum = 0;

    subjectGroups.forEach(({ y, X }) => {
      const n = y.length;
      const fitted = matVecMul(X, beta);
      const residuals = y.map((val, idx) => val - (fitted[idx] ?? 0));
      const sumRes = residuals.reduce((sum, val) => sum + val, 0);
      const denom = sigmaE2 + n * sigmaB2;
      const bHat = (sigmaB2 * sumRes) / denom;
      const bVar = (sigmaB2 * sigmaE2) / denom;
      sigmaB2Sum += bHat * bHat + bVar;
      const residualSum = residuals.reduce((sum, val) => sum + Math.pow(val - bHat, 2), 0);
      sigmaE2Sum += residualSum + n * bVar;
    });

    const newSigmaB2 = Math.max(sigmaB2Sum / subjectGroups.size, 1e-6);
    const newSigmaE2 = Math.max(sigmaE2Sum / data.length, 1e-6);
    const change = Math.max(
      Math.abs((newSigmaB2 - sigmaB2) / sigmaB2),
      Math.abs((newSigmaE2 - sigmaE2) / sigmaE2)
    );
    sigmaB2 = newSigmaB2;
    sigmaE2 = newSigmaE2;
    if (change < tol) break;
  }

  let XtVinvX = createMatrix(p, p, 0);
  subjectGroups.forEach(({ y, X }) => {
    const n = y.length;
    const a = sigmaE2;
    const b = sigmaB2;
    const weight1 = 1 / a;
    const weight2 = b / (a * (a + n * b));
    const Xt = transpose(X);
    const XtX = matMul(Xt, X);
    const ones = Array.from({ length: n }, () => 1);
    const Xt1 = matVecMul(Xt, ones);
    XtVinvX = matAdd(XtVinvX, XtX, weight1);
    XtVinvX = matAdd(XtVinvX, outerProduct(Xt1, Xt1), -weight2);
  });

  const XtVinvXInv = matInverse(XtVinvX);
  const dfError = data.length - p;
  if (dfError <= 0) {
    throw new KeisukeAdapterError('lmmInsufficientData');
  }

  const dfBetween = groupLevels.length - 1;
  const contrast = createMatrix(dfBetween, p, 0);
  for (let i = 0; i < dfBetween; i += 1) {
    contrast[i][1 + i] = 1;
  }

  const betaC = matVecMul(contrast, beta);
  const covC = matMul(matMul(contrast, XtVinvXInv), transpose(contrast));
  const covCInv = matInverse(covC);
  const stat = vecDot(betaC, matVecMul(covCInv, betaC));
  const f = stat / dfBetween;
  const pValue = calculatePValue(f, dfBetween, dfError);

  const msWithin = sigmaE2;
  const ssBetween = f * msWithin * dfBetween;
  const ssWithin = msWithin * dfError;

  const groupValues: Record<string, number[]> = {};
  groupLevels.forEach((level) => {
    groupValues[level] = [];
  });
  data.forEach((row) => {
    groupValues[row.group].push(row.value);
  });

  const groupStats = groupLevels.map((level) => {
    const values = groupValues[level];
    const nGroup = values.length;
    const meanGroup = values.reduce((sum, v) => sum + v, 0) / nGroup;
    const varianceGroup =
      values.reduce((sum, v) => sum + Math.pow(v - meanGroup, 2), 0) / Math.max(1, nGroup - 1);
    return { name: level, mean: meanGroup, n: nGroup, stdDev: Math.sqrt(varianceGroup) };
  });

  return {
    f,
    pValue,
    dfBetween,
    dfWithin: dfError,
    ssBetween,
    ssWithin,
    msBetween: ssBetween / dfBetween,
    msWithin,
    design: 'lmm',
    varianceComponents: {
      randomIntercept: sigmaB2,
      residual: sigmaE2,
    },
    groups: groupStats,
  };
}

/**
 * 二元配置分散分析（Two-way ANOVA）
 */
export function twoWayANOVA(data: { factorA: string; factorB: string; value: number }[]): TwoWayANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  const factorALevels = Array.from(new Set(data.map((row) => row.factorA)));
  const factorBLevels = Array.from(new Set(data.map((row) => row.factorB)));

  if (factorALevels.length < 2 || factorBLevels.length < 2) {
    throw new KeisukeAdapterError('minTwoLevelsPerFactor');
  }

  const overallMean = data.reduce((sum, row) => sum + row.value, 0) / data.length;

  const cellsMap = new Map<string, number[]>();
  data.forEach((row) => {
    const key = `${row.factorA}|||${row.factorB}`;
    if (!cellsMap.has(key)) {
      cellsMap.set(key, []);
    }
    cellsMap.get(key)?.push(row.value);
  });

  const cells: TwoWayANOVAResult['cells'] = [];
  cellsMap.forEach((values, key) => {
    const [factorA, factorB] = key.split('|||');
    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    cells.push({ factorA, factorB, n: values.length, mean });
  });

  const factorAStats = new Map<string, { n: number; mean: number }>();
  factorALevels.forEach((level) => {
    const values = data.filter((row) => row.factorA === level).map((row) => row.value);
    factorAStats.set(level, {
      n: values.length,
      mean: values.reduce((sum, v) => sum + v, 0) / values.length,
    });
  });

  const factorBStats = new Map<string, { n: number; mean: number }>();
  factorBLevels.forEach((level) => {
    const values = data.filter((row) => row.factorB === level).map((row) => row.value);
    factorBStats.set(level, {
      n: values.length,
      mean: values.reduce((sum, v) => sum + v, 0) / values.length,
    });
  });

  const ssA = Array.from(factorAStats.entries()).reduce(
    (sum, [, stats]) => sum + stats.n * Math.pow(stats.mean - overallMean, 2),
    0
  );

  const ssB = Array.from(factorBStats.entries()).reduce(
    (sum, [, stats]) => sum + stats.n * Math.pow(stats.mean - overallMean, 2),
    0
  );

  const ssAB = cells.reduce((sum, cell) => {
    const meanA = factorAStats.get(cell.factorA)?.mean ?? overallMean;
    const meanB = factorBStats.get(cell.factorB)?.mean ?? overallMean;
    const interactionEffect = cell.mean - meanA - meanB + overallMean;
    return sum + cell.n * Math.pow(interactionEffect, 2);
  }, 0);

  const ssWithin = data.reduce((sum, row) => {
    const cellMean =
      cells.find((cell) => cell.factorA === row.factorA && cell.factorB === row.factorB)?.mean ?? overallMean;
    return sum + Math.pow(row.value - cellMean, 2);
  }, 0);

  const a = factorALevels.length;
  const b = factorBLevels.length;
  const cellCount = cells.length;
  const dfA = a - 1;
  const dfB = b - 1;
  const dfAB = dfA * dfB;
  const dfWithin = data.length - cellCount;
  if (dfWithin <= 0) {
    throw new KeisukeAdapterError('twoWayAnovaErrorDfNonPositive');
  }

  const msA = ssA / dfA;
  const msB = ssB / dfB;
  const msAB = ssAB / dfAB;
  const msWithin = ssWithin / dfWithin;

  const fA = msA / msWithin;
  const fB = msB / msWithin;
  const fAB = msAB / msWithin;

  return {
    factorA: {
      name: 'Factor A',
      ss: ssA,
      df: dfA,
      ms: msA,
      f: fA,
      pValue: calculatePValue(fA, dfA, dfWithin),
      errorDf: dfWithin,
    },
    factorB: {
      name: 'Factor B',
      ss: ssB,
      df: dfB,
      ms: msB,
      f: fB,
      pValue: calculatePValue(fB, dfB, dfWithin),
      errorDf: dfWithin,
    },
    interaction: {
      name: 'Interaction',
      ss: ssAB,
      df: dfAB,
      ms: msAB,
      f: fAB,
      pValue: calculatePValue(fAB, dfAB, dfWithin),
      errorDf: dfWithin,
    },
    within: {
      name: 'Within',
      ss: ssWithin,
      df: dfWithin,
      ms: msWithin,
      f: NaN,
      pValue: NaN,
    },
    design: 'independent',
    overallMean,
    factorALevels,
    factorBLevels,
    cells,
  };
}

/**
 * 完全反復測定 2-way ANOVA（因子A・因子Bともに被験体内）
 */
export function fullRepeatedTwoWayANOVA(data: {
  factorA: string;
  factorB: string;
  subject: string;
  value: number;
}[]): TwoWayANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  const factorALevels = Array.from(new Set(data.map((row) => row.factorA)));
  const factorBLevels = Array.from(new Set(data.map((row) => row.factorB)));
  const subjects = Array.from(new Set(data.map((row) => row.subject)));

  if (factorALevels.length < 2 || factorBLevels.length < 2) {
    throw new KeisukeAdapterError('minTwoLevelsPerFactor');
  }

  const subjectMap = new Map<string, Map<string, number>>();
  data.forEach((row) => {
    const key = row.subject;
    if (!subjectMap.has(key)) {
      subjectMap.set(key, new Map());
    }
    const cellKey = `${row.factorA}|||${row.factorB}`;
    if (subjectMap.get(key)?.has(cellKey)) {
      throw new KeisukeAdapterError('duplicateSubjectCondition');
    }
    subjectMap.get(key)?.set(cellKey, row.value);
  });

  subjects.forEach((subject) => {
    const entries = subjectMap.get(subject);
    factorALevels.forEach((a) => {
      factorBLevels.forEach((b) => {
        const key = `${a}|||${b}`;
        if (!entries?.has(key)) {
          throw new KeisukeAdapterError('missingWithinSubject');
        }
      });
    });
  });

  const n = subjects.length;
  if (n < 2) {
    throw new KeisukeAdapterError('fullRepeatedMinSubjects');
  }

  const a = factorALevels.length;
  const b = factorBLevels.length;

  const cells: TwoWayANOVAResult['cells'] = [];
  factorALevels.forEach((levelA) => {
    factorBLevels.forEach((levelB) => {
      const values = subjects.map((subject) => {
        const key = `${levelA}|||${levelB}`;
        return subjectMap.get(subject)?.get(key) ?? 0;
      });
      const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
      cells.push({ factorA: levelA, factorB: levelB, n: values.length, mean });
    });
  });

  const grandMean = cells.reduce((sum, cell) => sum + cell.mean, 0) / cells.length;

  const subjectMeans = new Map<string, number>();
  subjects.forEach((subject) => {
    let total = 0;
    factorALevels.forEach((levelA) => {
      factorBLevels.forEach((levelB) => {
        const key = `${levelA}|||${levelB}`;
        total += subjectMap.get(subject)?.get(key) ?? 0;
      });
    });
    subjectMeans.set(subject, total / (a * b));
  });

  const factorAMeans = new Map<string, number>();
  factorALevels.forEach((levelA) => {
    let total = 0;
    subjects.forEach((subject) => {
      factorBLevels.forEach((levelB) => {
        const key = `${levelA}|||${levelB}`;
        total += subjectMap.get(subject)?.get(key) ?? 0;
      });
    });
    factorAMeans.set(levelA, total / (n * b));
  });

  const factorBMeans = new Map<string, number>();
  factorBLevels.forEach((levelB) => {
    let total = 0;
    subjects.forEach((subject) => {
      factorALevels.forEach((levelA) => {
        const key = `${levelA}|||${levelB}`;
        total += subjectMap.get(subject)?.get(key) ?? 0;
      });
    });
    factorBMeans.set(levelB, total / (n * a));
  });

  const ssTotal = subjects.reduce((sum, subject) => {
    let total = 0;
    factorALevels.forEach((levelA) => {
      factorBLevels.forEach((levelB) => {
        const key = `${levelA}|||${levelB}`;
        const value = subjectMap.get(subject)?.get(key) ?? 0;
        total += Math.pow(value - grandMean, 2);
      });
    });
    return sum + total;
  }, 0);

  const ssSubjects =
    a *
    b *
    subjects.reduce((sum, subject) => {
      const meanSubject = subjectMeans.get(subject) ?? grandMean;
      return sum + Math.pow(meanSubject - grandMean, 2);
    }, 0);

  const ssA =
    b *
    n *
    factorALevels.reduce((sum, levelA) => {
      const meanA = factorAMeans.get(levelA) ?? grandMean;
      return sum + Math.pow(meanA - grandMean, 2);
    }, 0);

  const ssB =
    a *
    n *
    factorBLevels.reduce((sum, levelB) => {
      const meanB = factorBMeans.get(levelB) ?? grandMean;
      return sum + Math.pow(meanB - grandMean, 2);
    }, 0);

  const ssAB =
    n *
    cells.reduce((sum, cell) => {
      const meanA = factorAMeans.get(cell.factorA) ?? grandMean;
      const meanB = factorBMeans.get(cell.factorB) ?? grandMean;
      const interaction = cell.mean - meanA - meanB + grandMean;
      return sum + Math.pow(interaction, 2);
    }, 0);

  const ssAxS = subjects.reduce((sum, subject) => {
    const meanSubject = subjectMeans.get(subject) ?? grandMean;
    const part = factorALevels.reduce((subSum, levelA) => {
      const meanA = factorAMeans.get(levelA) ?? grandMean;
      let meanSubjectA = 0;
      factorBLevels.forEach((levelB) => {
        const key = `${levelA}|||${levelB}`;
        meanSubjectA += subjectMap.get(subject)?.get(key) ?? 0;
      });
      meanSubjectA /= b;
      return subSum + Math.pow(meanSubjectA - meanSubject - meanA + grandMean, 2);
    }, 0);
    return sum + b * part;
  }, 0);

  const ssBxS = subjects.reduce((sum, subject) => {
    const meanSubject = subjectMeans.get(subject) ?? grandMean;
    const part = factorBLevels.reduce((subSum, levelB) => {
      const meanB = factorBMeans.get(levelB) ?? grandMean;
      let meanSubjectB = 0;
      factorALevels.forEach((levelA) => {
        const key = `${levelA}|||${levelB}`;
        meanSubjectB += subjectMap.get(subject)?.get(key) ?? 0;
      });
      meanSubjectB /= a;
      return subSum + Math.pow(meanSubjectB - meanSubject - meanB + grandMean, 2);
    }, 0);
    return sum + a * part;
  }, 0);

  const ssABxS = ssTotal - ssSubjects - ssA - ssB - ssAB - ssAxS - ssBxS;

  const dfA = a - 1;
  const dfB = b - 1;
  const dfAB = dfA * dfB;
  const dfSubjects = n - 1;
  const dfAxS = dfA * dfSubjects;
  const dfBxS = dfB * dfSubjects;
  const dfABxS = dfAB * dfSubjects;

  const msA = ssA / dfA;
  const msB = ssB / dfB;
  const msAB = ssAB / dfAB;
  const msAxS = ssAxS / dfAxS;
  const msBxS = ssBxS / dfBxS;
  const msABxS = ssABxS / dfABxS;

  const fA = msA / msAxS;
  const fB = msB / msBxS;
  const fAB = msAB / msABxS;

  return {
    factorA: {
      name: 'Factor A',
      ss: ssA,
      df: dfA,
      ms: msA,
      f: fA,
      pValue: calculatePValue(fA, dfA, dfAxS),
      errorDf: dfAxS,
    },
    factorB: {
      name: 'Factor B',
      ss: ssB,
      df: dfB,
      ms: msB,
      f: fB,
      pValue: calculatePValue(fB, dfB, dfBxS),
      errorDf: dfBxS,
    },
    interaction: {
      name: 'Interaction',
      ss: ssAB,
      df: dfAB,
      ms: msAB,
      f: fAB,
      pValue: calculatePValue(fAB, dfAB, dfABxS),
      errorDf: dfABxS,
    },
    within: {
      name: '因子A×因子B×被験体',
      ss: ssABxS,
      df: dfABxS,
      ms: msABxS,
      f: NaN,
      pValue: NaN,
    },
    errorTerms: [
      {
        name: '因子A×被験体',
        ss: ssAxS,
        df: dfAxS,
        ms: msAxS,
        f: NaN,
        pValue: NaN,
      },
      {
        name: '因子B×被験体',
        ss: ssBxS,
        df: dfBxS,
        ms: msBxS,
        f: NaN,
        pValue: NaN,
      },
    ],
    design: 'full-repeated',
    subjectCount: n,
    overallMean: grandMean,
    factorALevels,
    factorBLevels,
    cells,
  };
}

/**
 * 線形混合効果モデル（ランダム切片）
 */
export function lmmTwoWayANOVA(data: {
  factorA: string;
  factorB: string;
  subject: string;
  value: number;
}[]): TwoWayANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  const factorALevels = Array.from(new Set(data.map((row) => row.factorA)));
  const factorBLevels = Array.from(new Set(data.map((row) => row.factorB)));
  const subjects = Array.from(new Set(data.map((row) => row.subject)));

  if (factorALevels.length < 2 || factorBLevels.length < 2) {
    throw new KeisukeAdapterError('minTwoLevelsPerFactor');
  }

  const subjectGroups = new Map<string, { y: number[]; X: Matrix }>();
  data.forEach((row) => {
    const rowX = buildTwoWayDesignRow(row.factorA, row.factorB, factorALevels, factorBLevels);
    if (!subjectGroups.has(row.subject)) {
      subjectGroups.set(row.subject, { y: [], X: [] });
    }
    const entry = subjectGroups.get(row.subject);
    entry?.y.push(row.value);
    entry?.X.push(rowX);
  });

  const subjectCount = subjectGroups.size;
  if (subjectCount < 2) {
    throw new KeisukeAdapterError('lmmMinSubjects');
  }

  const allValues = data.map((row) => row.value);
  const mean = allValues.reduce((sum, v) => sum + v, 0) / allValues.length;
  const variance =
    allValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / Math.max(1, allValues.length - 1);

  let sigmaE2 = Math.max(variance * 0.5, 1e-6);
  let sigmaB2 = Math.max(variance * 0.5, 1e-6);

  const maxIter = 50;
  const tol = 1e-6;
  const p = buildTwoWayDesignRow(factorALevels[0], factorBLevels[0], factorALevels, factorBLevels).length;
  let beta = Array.from({ length: p }, () => 0);

  for (let iter = 0; iter < maxIter; iter += 1) {
    let XtVinvX = createMatrix(p, p, 0);
    let XtVinvY = Array.from({ length: p }, () => 0);

    subjectGroups.forEach(({ y, X }) => {
      const n = y.length;
      const a = sigmaE2;
      const b = sigmaB2;
      const weight1 = 1 / a;
      const weight2 = b / (a * (a + n * b));
      const Xt = transpose(X);
      const XtX = matMul(Xt, X);
      const ones = Array.from({ length: n }, () => 1);
      const Xt1 = matVecMul(Xt, ones);
      XtVinvX = matAdd(XtVinvX, XtX, weight1);
      XtVinvX = matAdd(XtVinvX, outerProduct(Xt1, Xt1), -weight2);

      const Xty = matVecMul(Xt, y);
      const sumY = y.reduce((sum, v) => sum + v, 0);
      XtVinvY = XtVinvY.map((value, idx) => value + weight1 * Xty[idx] - weight2 * Xt1[idx] * sumY);
    });

    const XtVinvXInv = matInverse(XtVinvX);
    beta = matVecMul(XtVinvXInv, XtVinvY);

    let sigmaB2Sum = 0;
    let sigmaE2Sum = 0;

    subjectGroups.forEach(({ y, X }) => {
      const n = y.length;
      const fitted = matVecMul(X, beta);
      const residuals = y.map((val, idx) => val - (fitted[idx] ?? 0));
      const sumRes = residuals.reduce((sum, val) => sum + val, 0);
      const denom = sigmaE2 + n * sigmaB2;
      const bHat = (sigmaB2 * sumRes) / denom;
      const bVar = (sigmaB2 * sigmaE2) / denom;
      sigmaB2Sum += bHat * bHat + bVar;
      const residualSum = residuals.reduce((sum, val) => sum + Math.pow(val - bHat, 2), 0);
      sigmaE2Sum += residualSum + n * bVar;
    });

    const newSigmaB2 = Math.max(sigmaB2Sum / subjectCount, 1e-6);
    const newSigmaE2 = Math.max(sigmaE2Sum / data.length, 1e-6);
    const change = Math.max(
      Math.abs((newSigmaB2 - sigmaB2) / sigmaB2),
      Math.abs((newSigmaE2 - sigmaE2) / sigmaE2)
    );
    sigmaB2 = newSigmaB2;
    sigmaE2 = newSigmaE2;
    if (change < tol) break;
  }

  let XtVinvX = createMatrix(p, p, 0);
  subjectGroups.forEach(({ y, X }) => {
    const n = y.length;
    const a = sigmaE2;
    const b = sigmaB2;
    const weight1 = 1 / a;
    const weight2 = b / (a * (a + n * b));
    const Xt = transpose(X);
    const XtX = matMul(Xt, X);
    const ones = Array.from({ length: n }, () => 1);
    const Xt1 = matVecMul(Xt, ones);
    XtVinvX = matAdd(XtVinvX, XtX, weight1);
    XtVinvX = matAdd(XtVinvX, outerProduct(Xt1, Xt1), -weight2);
  });

  const XtVinvXInv = matInverse(XtVinvX);
  const dfError = data.length - p;
  if (dfError <= 0) {
    throw new KeisukeAdapterError('lmmInsufficientData');
  }

  const aCols = factorALevels.length - 1;
  const bCols = factorBLevels.length - 1;
  const abCols = aCols * bCols;
  const idxAStart = 1;
  const idxBStart = idxAStart + aCols;
  const idxABStart = idxBStart + bCols;

  const buildContrast = (start: number, size: number): Matrix => {
    const contrast = createMatrix(size, p, 0);
    for (let i = 0; i < size; i += 1) {
      contrast[i][start + i] = 1;
    }
    return contrast;
  };

  const calcWaldF = (contrast: Matrix): { f: number; pValue: number } => {
    const betaC = matVecMul(contrast, beta);
    const covC = matMul(matMul(contrast, XtVinvXInv), transpose(contrast));
    const covCInv = matInverse(covC);
    const stat = vecDot(betaC, matVecMul(covCInv, betaC));
    const df1 = contrast.length;
    const f = stat / df1;
    return { f, pValue: calculatePValue(f, df1, dfError) };
  };

  const testA = calcWaldF(buildContrast(idxAStart, aCols));
  const testB = calcWaldF(buildContrast(idxBStart, bCols));
  const testAB = calcWaldF(buildContrast(idxABStart, abCols));

  // LMMでは固定効果の検定はWald検定（近似F）による。古典的ANOVAのSS/MS分解は適用されない。
  // SS/MSは表示しない（omitSSMSTable を参照）。誤差項の分散成分は varianceComponents で提示。
  const cells: TwoWayANOVAResult['cells'] = [];
  factorALevels.forEach((levelA) => {
    factorBLevels.forEach((levelB) => {
      const values = data
        .filter((row) => row.factorA === levelA && row.factorB === levelB)
        .map((row) => row.value);
      const meanCell = values.reduce((sum, v) => sum + v, 0) / Math.max(1, values.length);
      cells.push({ factorA: levelA, factorB: levelB, n: values.length, mean: meanCell });
    });
  });

  const msError = sigmaE2;
  return {
    factorA: {
      name: 'Factor A',
      ss: 0,
      df: aCols,
      ms: 0,
      f: testA.f,
      pValue: testA.pValue,
      errorDf: dfError,
    },
    factorB: {
      name: 'Factor B',
      ss: 0,
      df: bCols,
      ms: 0,
      f: testB.f,
      pValue: testB.pValue,
      errorDf: dfError,
    },
    interaction: {
      name: 'Interaction',
      ss: 0,
      df: abCols,
      ms: 0,
      f: testAB.f,
      pValue: testAB.pValue,
      errorDf: dfError,
    },
    within: {
      name: '残差',
      ss: msError * dfError,
      df: dfError,
      ms: msError,
      f: NaN,
      pValue: NaN,
    },
    errorTerms: [
      {
        name: '被験体（ランダム切片）',
        ss: sigmaB2 * Math.max(1, subjectCount - 1),
        df: Math.max(1, subjectCount - 1),
        ms: sigmaB2,
        f: NaN,
        pValue: NaN,
      },
    ],
    design: 'lmm',
    omitSSMSTable: true,
    subjectCount,
    varianceComponents: {
      randomIntercept: sigmaB2,
      residual: sigmaE2,
    },
    overallMean: mean,
    factorALevels,
    factorBLevels,
    cells,
  };
}

/**
 * 混合デザイン 2-way ANOVA（因子A: 被験体間、因子B: 被験体内）
 */
export function mixedDesignTwoWayANOVA(data: {
  factorA: string;
  factorB: string;
  subject: string;
  value: number;
}[]): TwoWayANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  const factorALevels = Array.from(new Set(data.map((row) => row.factorA)));
  const factorBLevels = Array.from(new Set(data.map((row) => row.factorB)));

  if (factorALevels.length < 2 || factorBLevels.length < 2) {
    throw new KeisukeAdapterError('minTwoLevelsPerFactor');
  }

  const subjectsMap = new Map<string, { factorA: string; values: Map<string, number> }>();
  data.forEach((row) => {
    if (!row.subject) return;
    if (!subjectsMap.has(row.subject)) {
      subjectsMap.set(row.subject, { factorA: row.factorA, values: new Map() });
    }
    const subjectEntry = subjectsMap.get(row.subject);
    if (!subjectEntry) return;
    if (subjectEntry.factorA !== row.factorA) {
      throw new KeisukeAdapterError('subjectMultipleFactorALevels');
    }
    if (subjectEntry.values.has(row.factorB)) {
      throw new KeisukeAdapterError('duplicateSubjectFactorB');
    }
    subjectEntry.values.set(row.factorB, row.value);
  });

  const subjects = Array.from(subjectsMap.entries()).map(([subject, entry]) => ({
    subject,
    factorA: entry.factorA,
    values: entry.values,
  }));

  const subjectCount = subjects.length;
  if (subjectCount === 0) {
    throw new KeisukeAdapterError('subjectNotIdentifiable');
  }

  subjects.forEach((subject) => {
    factorBLevels.forEach((level) => {
      if (!subject.values.has(level)) {
        throw new KeisukeAdapterError('rmMissingWithinSubject');
      }
    });
  });

  const grandTotal = subjects.reduce((sum, subject) => {
    const subjectSum = Array.from(subject.values.values()).reduce((acc, value) => acc + value, 0);
    return sum + subjectSum;
  }, 0);
  const grandMean = grandTotal / (subjectCount * factorBLevels.length);

  const groupSubjects = new Map<string, typeof subjects>();
  subjects.forEach((subject) => {
    if (!groupSubjects.has(subject.factorA)) {
      groupSubjects.set(subject.factorA, []);
    }
    groupSubjects.get(subject.factorA)?.push(subject);
  });

  const groupMeans = new Map<string, number>();
  groupSubjects.forEach((group, factorA) => {
    const sum = group.reduce((acc, subject) => {
      const subjectSum = Array.from(subject.values.values()).reduce((v, value) => v + value, 0);
      return acc + subjectSum;
    }, 0);
    groupMeans.set(factorA, sum / (group.length * factorBLevels.length));
  });

  const subjectMeans = new Map<string, number>();
  subjects.forEach((subject) => {
    const sum = Array.from(subject.values.values()).reduce((acc, value) => acc + value, 0);
    subjectMeans.set(subject.subject, sum / factorBLevels.length);
  });

  const factorBMeans = new Map<string, number>();
  factorBLevels.forEach((level) => {
    const sum = subjects.reduce((acc, subject) => acc + (subject.values.get(level) ?? 0), 0);
    factorBMeans.set(level, sum / subjectCount);
  });

  const cells: TwoWayANOVAResult['cells'] = [];
  groupSubjects.forEach((group, factorA) => {
    factorBLevels.forEach((factorB) => {
      const values = group.map((subject) => subject.values.get(factorB) ?? 0);
      const mean = values.reduce((acc, value) => acc + value, 0) / values.length;
      cells.push({ factorA, factorB, n: values.length, mean });
    });
  });

  const ssA = Array.from(groupSubjects.entries()).reduce((sum, [, group]) => {
    const factorA = group[0]?.factorA ?? '';
    const n = group.length;
    const meanA = groupMeans.get(factorA) ?? grandMean;
    return sum + factorBLevels.length * n * Math.pow(meanA - grandMean, 2);
  }, 0);

  const ssSubjects = Array.from(groupSubjects.entries()).reduce((sum, [, group]) => {
    const factorA = group[0]?.factorA ?? '';
    const meanA = groupMeans.get(factorA) ?? grandMean;
    const groupSum = group.reduce((acc, subject) => {
      const meanSubject = subjectMeans.get(subject.subject) ?? meanA;
      return acc + Math.pow(meanSubject - meanA, 2);
    }, 0);
    return sum + factorBLevels.length * groupSum;
  }, 0);

  const ssB = factorBLevels.reduce((sum, level) => {
    const meanB = factorBMeans.get(level) ?? grandMean;
    return sum + subjectCount * Math.pow(meanB - grandMean, 2);
  }, 0);

  const ssAB = cells.reduce((sum, cell) => {
    const meanA = groupMeans.get(cell.factorA) ?? grandMean;
    const meanB = factorBMeans.get(cell.factorB) ?? grandMean;
    const interaction = cell.mean - meanA - meanB + grandMean;
    return sum + cell.n * Math.pow(interaction, 2);
  }, 0);

  const ssTotal = subjects.reduce((sum, subject) => {
    const subjectSum = Array.from(subject.values.values()).reduce((acc, value) => acc + Math.pow(value - grandMean, 2), 0);
    return sum + subjectSum;
  }, 0);

  const ssBSubjects = ssTotal - ssA - ssSubjects - ssB - ssAB;

  const a = factorALevels.length;
  const b = factorBLevels.length;
  const dfA = a - 1;
  const dfSubjects = subjectCount - a;
  const dfB = b - 1;
  const dfAB = dfA * dfB;
  const dfBSubjects = dfSubjects * dfB;

  if (dfSubjects <= 0 || dfBSubjects <= 0) {
    throw new KeisukeAdapterError('mixedDesignMinSubjectsPerGroup');
  }

  const msA = ssA / dfA;
  const msSubjects = ssSubjects / dfSubjects;
  const msB = ssB / dfB;
  const msAB = ssAB / dfAB;
  const msBSubjects = ssBSubjects / dfBSubjects;

  const fA = msA / msSubjects;
  const fB = msB / msBSubjects;
  const fAB = msAB / msBSubjects;

  return {
    factorA: {
      name: 'Factor A',
      ss: ssA,
      df: dfA,
      ms: msA,
      f: fA,
      pValue: calculatePValue(fA, dfA, dfSubjects),
      errorDf: dfSubjects,
    },
    factorB: {
      name: 'Factor B',
      ss: ssB,
      df: dfB,
      ms: msB,
      f: fB,
      pValue: calculatePValue(fB, dfB, dfBSubjects),
      errorDf: dfBSubjects,
    },
    interaction: {
      name: 'Interaction',
      ss: ssAB,
      df: dfAB,
      ms: msAB,
      f: fAB,
      pValue: calculatePValue(fAB, dfAB, dfBSubjects),
      errorDf: dfBSubjects,
    },
    within: {
      name: '因子B×被験体（因子A内）',
      ss: ssBSubjects,
      df: dfBSubjects,
      ms: msBSubjects,
      f: NaN,
      pValue: NaN,
    },
    errorTerms: [
      {
        name: '被験体（因子A内）',
        ss: ssSubjects,
        df: dfSubjects,
        ms: msSubjects,
        f: NaN,
        pValue: NaN,
      },
    ],
    design: 'mixed',
    subjectCount,
    overallMean: grandMean,
    factorALevels,
    factorBLevels,
    cells,
  };
}

